import React from 'react';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { EspecieService } from '../services/especieService';
import { type CalibreListItem } from '../types/especie';

export interface CalibreSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** ID of the presentacion to filter calibres */
  idPresentacion?: number | string;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter calibres */
  filter?: (calibre: CalibreListItem) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * CalibreSelect Component
 *
 * A reusable dropdown component that displays calibres filtered by presentacion.
 * Features:
 * - Automatic data loading based on selected presentacion
 * - Loading states and error handling
 * - Dynamic filtering by presentacion ID
 * - Additional custom filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 * - Automatic reset when presentacion changes
 */
export const CalibreSelect: React.FC<CalibreSelectProps> = ({
  value,
  onChange,
  idPresentacion,
  label = 'Calibre',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const [calibres, setCalibres] = React.useState<CalibreListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch calibres when idPresentacion changes
  const fetchCalibres = React.useCallback(async () => {
    if (!idPresentacion || idPresentacion === '') {
      setCalibres([]);
      return;
    }

    try {
      setLoading(true);
      const presentacionId = typeof idPresentacion === 'string' ? parseInt(idPresentacion) : idPresentacion;

      // Validate that presentacionId is a valid number
      if (isNaN(presentacionId) || presentacionId <= 0) {
        console.warn('Invalid presentacion ID:', idPresentacion);
        setCalibres([]);
        return;
      }

      const data = await EspecieService.getCalibresByPresentacion(presentacionId);
      setCalibres(data);
    } catch (error) {
      console.error('Error fetching calibres by presentacion:', error);
      setCalibres([]);
    } finally {
      setLoading(false);
    }
  }, [idPresentacion]);

  React.useEffect(() => {
    fetchCalibres();
  }, [fetchCalibres]);

  // Reset selected value when presentacion changes
  const prevIdPresentacionRef = React.useRef<number | string | undefined>(idPresentacion);

  React.useEffect(() => {
    // Only reset if idPresentacion actually changed (not on initial mount)
    if (prevIdPresentacionRef.current !== undefined && prevIdPresentacionRef.current !== idPresentacion) {
      if (value && value !== '') {
        // Clear the selected calibre when presentacion changes
        onChange('');
      }
    }
    prevIdPresentacionRef.current = idPresentacion;
  }, [idPresentacion, value, onChange]);

  // Filter calibres based on props
  const filteredCalibres = React.useMemo(() => {
    let filtered = calibres;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [calibres, filter]);

  // Convert calibres to select options
  const options = React.useMemo(() => {
    return filteredCalibres.map((calibre) => ({
      value: calibre.idCalibre,
      label: calibre.descripcion,
    }));
  }, [filteredCalibres]);

  // Handle error state
  const displayError = error;

  // Component is disabled if no presentacion is selected, it's explicitly disabled, or it's loading
  const isDisabled = disabled || loading || !idPresentacion || idPresentacion === '';

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={isDisabled}
      placeholder={placeholder || (!idPresentacion || idPresentacion === '' ? 'Selecciona una presentación primero' : undefined)}
      loading={loading}
      sx={sx}
    />
  );
};

export default CalibreSelect;